import { useCallback, useState } from 'react';
import { RotateCcw, Download } from 'lucide-react';
import useAsync from '../../hooks/useAsync';
import documentService from '../../services/documentService';
import { PanelSkeleton } from '../common/LoadingState';
import ConfirmDialog from '../common/ConfirmDialog';
import PermissionGuard from '../common/PermissionGuard';
import { useToast } from '../../context/ToastContext';
import { triggerDownload } from '../../utils/download';
import { formatDate, formatBytes } from '../../utils/format';
import { P } from '../../utils/permissions';
import cn from '../../utils/cn';

/**
 * v2 versions list: 28px circle "vN" (accent for current), note, "by · date · size",
 * `.pill-ok` Current, Download for any version, Restore for older versions.
 * Restoring copies the old content forward as a NEW version — history is never rewritten.
 */
export default function VersionHistory({ document: doc, onChanged, framed = true }) {
  const toast = useToast();
  const [target, setTarget] = useState(null);
  const { data: versions, loading, reload } = useAsync(() => documentService.listVersions(doc.id), [doc.id, doc.versionCount]);

  const restore = useCallback(async () => {
    await documentService.restoreVersion(doc.id, target.id);
    toast.success(`Restored from v${target.versionNo} — created a new version`);
    reload();
    onChanged?.();
  }, [doc.id, target, toast, reload, onChanged]);

  const download = async (v) => {
    try {
      const { url } = await documentService.versionDownloadUrl(doc.id, v.id);
      if (url) triggerDownload(url, doc.name);
      toast.success(`Downloading v${v.versionNo} of "${doc.name}"`);
    } catch (err) {
      toast.error(err.message || 'That version could not be downloaded.');
    }
  };

  const list = versions || [];

  const body = (
    <>
      {loading && !versions && (
        <div style={{ padding: 16 }}>
          <PanelSkeleton rows={3} />
        </div>
      )}
      {list.map((v) => (
        <div key={v.id} className="side-list__item">
          <div className="row" style={{ gap: 10, minWidth: 0 }}>
            <span className={cn('vcircle', v.isCurrent && 'is-current')}>v{v.versionNo}</span>
            <div style={{ minWidth: 0 }}>
              <div className="truncate" style={{ fontSize: 13, fontWeight: 600 }}>
                {v.changeNote || 'Uploaded'}
              </div>
              <div className="muted" style={{ fontSize: 12 }}>
                {v.uploadedByUser?.name} · {formatDate(v.createdAt)} · {formatBytes(v.sizeBytes)}
              </div>
            </div>
          </div>
          <div className="row" style={{ gap: 2, flex: 'none' }}>
            {v.isCurrent && <span className="pill pill-ok">Current</span>}
            <PermissionGuard permission={P.DOCUMENT_DOWNLOAD} resource={doc} capability="download">
              <button type="button" className="btn--icon" style={{ width: 28, height: 28 }} onClick={() => download(v)} aria-label={`Download version ${v.versionNo}`} title="Download this version">
                <Download size={14} />
              </button>
            </PermissionGuard>
            {!v.isCurrent && (
              <PermissionGuard permission={P.DOCUMENT_REPLACE} resource={doc} capability="replace">
                <button type="button" className="link" onClick={() => setTarget(v)}>
                  Restore
                </button>
              </PermissionGuard>
            )}
          </div>
        </div>
      ))}

      <ConfirmDialog
        open={Boolean(target)}
        onClose={() => setTarget(null)}
        onConfirm={restore}
        icon={RotateCcw}
        title={`Restore version ${target?.versionNo}?`}
        message={`This creates version ${(list[0]?.versionNo || 0) + 1} containing the contents of version ${target?.versionNo}.`}
        detail="Nothing is overwritten — every earlier version stays in this list."
        confirmLabel="Restore version"
      />
    </>
  );

  if (!framed) return body;

  return (
    <div className="panel side-list">
      <div className="side-list__head">
        <h3>Versions</h3>
        <span className="muted" style={{ fontSize: 12 }}>
          {list.length} total
        </span>
      </div>
      {body}
    </div>
  );
}
